import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import logo from '../Images/login-logo.png';
import './TeacherLogin.css';

const TeacherLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userInfo = localStorage.getItem('userInfo');

    // Helper to check JWT expiry
    function isTokenExpired(token) {
      if (!token) return true;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // exp is in seconds since epoch
        if (!payload.exp) return true;
        const now = Math.floor(Date.now() / 1000);
        return payload.exp < now;
      } catch (e) {
        return true;
      }
    }

    if (token && userInfo && userInfo !== 'undefined' && userInfo !== 'null') {
      if (isTokenExpired(token)) {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        return;
      }
      try {
        const user = JSON.parse(userInfo);
        if (user && user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (user) {
          navigate('/teacher/dashboard');
        }
      } catch (error) {
        console.error('Error parsing user info:', error);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
      }
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name] || errors.general) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        general: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ email: '', password: '', general: '' });

    try {
      // First, validate credentials without showing loading
      let response;
      let isAdmin = false;
      
      try {
        response = await axios.post('/api/auth/login', formData);
      } catch (teacherError) {
        // If teacher login fails, try admin login
        try {
          response = await axios.post('/api/auth/admin-login', formData);
          isAdmin = true;
        } catch (adminError) {
          throw adminError; // Throw the admin error if both fail
        }
      }
      
      if (response.data.success) {
        // Only show loading after credentials are validated
        setLoading(true);
        
        const { token, user, admin } = response.data;
        const userData = user || admin;
        
        // Store token and user info
        if (isAdmin || userData.role === 'admin' || userData.role === 'superadmin' || userData.is_admin) {
          localStorage.setItem('adminToken', token);
          localStorage.setItem('adminInfo', JSON.stringify(userData));
          localStorage.setItem('userRole', 'admin');
        } else {
          localStorage.setItem('token', token);
          localStorage.setItem('userInfo', JSON.stringify(userData));
        }
        
        toast.success('Login successful!');
        
        // Navigate based on user type
        if (isAdmin || userData.role === 'admin' || userData.role === 'superadmin' || userData.is_admin) {
          navigate('/admin/dashboard');
        } else {
          navigate('/teacher/dashboard');
        }
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid credentials. Please try again.';
      setErrors(prev => ({
        ...prev,
        general: errorMessage
      }));
      toast.error(errorMessage);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <h2>Signing you in...</h2>
          <p>Please wait while we authenticate your credentials</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left Column - Welcome Section with Animations */}
      <div className="login-left">
        <div className="bubbles">
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
        </div>
        
        <div className="welcome-content">
          <div className="company-logo">
            <img src={logo} alt="CurioSense Logo" />
          </div>
          <h1>Welcome to Curiosense Innovations</h1>
          <p>Empowering educators with comprehensive evaluation tools</p>
          
          <div className="features">
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div>
                <h3>Smart Analytics</h3>
                <p>Track student progress with detailed insights</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <div>
                <h3>Skill Assessment</h3>
                <p>Evaluate development across multiple domains</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">📋</div>
              <div>
                <h3>Detailed Reports</h3>
                <p>Generate comprehensive evaluation reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Login Form */}
      <div className="login-right">
        <div className="login-form-container">
          <div className="login-header">
            <h2>CurioKAARDS Teacher Portal</h2>
            <p>Access your evaluation dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">EMAIL</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={errors.email ? 'error' : ''}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">PASSWORD</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={errors.password ? 'error' : ''}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {showPassword ? (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </>
                    ) : (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            {errors.general && (
              <div className="error-message general-error">{errors.general}</div>
            )}

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Don't have an account?{' '}
              <a href="/teacher/register" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                Create account
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;
